import {
  WEIGHT_HAS_EXCEEDED_LIMIT,
  PAST_WEIGHT_LOSS_MEDICATION,
  DIABETIC_TYPE_1,
  PAST_ED_DIAGNOSIS,
  PREGNANCY,
  PAST_DIAGNOSIS,
  MULTISELECT_NONE_OF_THESE,
  CURRENTLY_PREGNANT,
  BREASTFEEDING,
  PHQ9B,
  SUICIDE1A
} from "../config/tags";
import {
  BLOOD_PRESSURE_DIA,
  BLOOD_PRESSURE_SYS,
  BLOOD_PRESSURE_UNKNOWN,
  MEDICAL,
  STATE
} from "../components/Profile/constants";
import {
  findAnswerByQuestionTag,
  findAnswerByTagAndByQuestionTag
} from "./questions";

export const RESULTS_ELI_SEQUENCE = "eliSequence";
export const RESULTS_ELI_WW = "eliWw";
export const RESULTS_ELI_NONE = "eliNone";
export const RESULTS_ELI_SUICIDE = "eliSuicide";
// Join sequence
export const STATE_UNSERVICEABLE = "stateUnserviceable";
export const STATE_HOTSPOT = "stateHotspot";
export const RESULTS_ELI_SEQUENCE_PREGNANT = "eliSequencePregnant";
export const RESULTS_ELI_SEQUENCE_BREAST_FEEDING = "eliSequenceBreastFeeding";
export const WAITLIST_REASON_IRRETRIEVABLE = "WAITLIST_REASON_IRRETRIEVABLE";
// Wait list reasons
export const WL_REASON_ELI_SUICIDE = "SUICIDE_HISTORY_OR_IDEATION";
export const WL_REASON_ELI_SEQUENCE_PREGNANT = "PREGNANT";
export const WL_REASON_ELI_SEQUENCE_BREAST_FEEDING = "BREASTFEEDING";
// These values must be kept equivalent to the clinical definition used in Sequence
export const HIGH_SYSTOLIC = 130;
export const HIGH_DIASTOLIC = 80;
export const HIGH_BMI = 27;
export const OBESE_BMI = 29.5;

export const isEligibleWw = (answers, profile, questions) => {
  const bmi = profile.bmi;

  if (bmi <= 18.5) {
    return false;
  }

  if (profile.gender === "F" || profile.gender === "O") {
    const currentlyPregnantAnswerObj = findAnswerByTagAndByQuestionTag(
      CURRENTLY_PREGNANT,
      PREGNANCY,
      answers,
      questions
    );

    if (currentlyPregnantAnswerObj) {
      return false;
    }
  }

  const bulimiaAnswer = findAnswerByQuestionTag(
    PAST_ED_DIAGNOSIS,
    answers,
    questions
  )?.toLowerCase();

  if (bulimiaAnswer === "yes") {
    return false;
  }

  return true;
};

export const isStateSupported = profile => {
  return profile[MEDICAL][STATE] !== "Virgin Islands";
};

export const getResultsPageEli = (result, sequenceEligible, wwEligible) => {
  if (result === WL_REASON_ELI_SUICIDE) {
    return RESULTS_ELI_SUICIDE;
  } else if (sequenceEligible) {
    return RESULTS_ELI_SEQUENCE;
  } else if (!sequenceEligible && wwEligible) {
    return RESULTS_ELI_WW;
  } else {
    return RESULTS_ELI_NONE;
  }
};

export const getResultsJoinSequence = (
  result,
  sequenceEligible,
  wwEligible
) => {
  if (result === WL_REASON_ELI_SUICIDE) {
    return RESULTS_ELI_SUICIDE;
  } else if (result === WL_REASON_ELI_SEQUENCE_PREGNANT) {
    return RESULTS_ELI_SEQUENCE_PREGNANT;
  } else if (result === WL_REASON_ELI_SEQUENCE_BREAST_FEEDING) {
    return RESULTS_ELI_SEQUENCE_BREAST_FEEDING;
  } else if (sequenceEligible) {
    return RESULTS_ELI_SEQUENCE;
  } else if (!sequenceEligible && wwEligible) {
    return RESULTS_ELI_WW;
  } else {
    return RESULTS_ELI_NONE;
  }
};

export const mainIsEligibleSequence = (answers, profile, questions) => {
  const { bmi } = profile;

  // DIFFERENT FROM SEQUENCE
  // This discrepancy still needs to be resolved between Sequence and WW
  if (!bmi || bmi <= 18.5) {
    return false;
  }

  // Reason: has type 1 Diabetes
  if (hasType1Diabetes(answers, questions)) return false;

  if (profile.gender === "F" || profile.gender === "O") {
    // Reason: is breastfeeding
    if (isBreastfeeding(answers, questions)) return false;

    // Reason: is pregnant
    if (isPregnant(answers, questions)) return false;
  }

  // ##BMIEligibilityCriteria = BMI-related eligibility:
  // - bmi < HIGH_BMI .............. eligible with history of high BMI
  //     AND recent use of weight loss medication
  // - HIGH_BMI <= bmi < OBESE_BMI . eligible with comorbidities
  //     OR recent use of weight loss medication
  // - OBESE_BMI <= bmi ............ eligibility not blocked on BMI

  if (bmi < HIGH_BMI) {
    // Reason: no history of high bmi
    if (!hasHistoryOfHighBmi(answers, questions)) return false;

    // Reason: healthy bmi
    if (!hasRecentHistoryOfWeightLossMedication(answers, questions))
      return false;
  }

  if (bmi < OBESE_BMI) {
    // Reason: no comorbidities
    if (
      !hasComorbidities(answers, questions, profile) &&
      !hasRecentHistoryOfWeightLossMedication(answers, questions)
    )
      return false;
  }

  // Reason: eating disorder
  if (hasEatingDisorder(answers, questions)) return false;

  return true;
};

const hasType1Diabetes = (answers, questions) =>
  findAnswerByTagAndByQuestionTag(
    DIABETIC_TYPE_1,
    PAST_DIAGNOSIS,
    answers,
    questions
  );
const isBreastfeeding = (answers, questions) =>
  findAnswerByQuestionTag(BREASTFEEDING, answers, questions)?.toLowerCase() ===
  "yes";
const isPregnant = (answers, questions) =>
  findAnswerByQuestionTag(PREGNANCY, answers, questions)?.toLowerCase() ===
  "yes";
const hasHistoryOfHighBmi = (answers, questions) =>
  findAnswerByQuestionTag(
    WEIGHT_HAS_EXCEEDED_LIMIT,
    answers,
    questions
  )?.toLowerCase() === "yes";
const hasRecentHistoryOfWeightLossMedication = (answers, questions) =>
  findAnswerByQuestionTag(
    PAST_WEIGHT_LOSS_MEDICATION,
    answers,
    questions
  )?.toLowerCase() === "yes";
const hasComorbidities = (answers, questions, profile) =>
  hasHighBloodPressure(profile) ||
  !findAnswerByTagAndByQuestionTag(
    MULTISELECT_NONE_OF_THESE,
    PAST_DIAGNOSIS,
    answers,
    questions
  );
const hasEatingDisorder = (answers, questions) =>
  findAnswerByQuestionTag(
    PAST_ED_DIAGNOSIS,
    answers,
    questions
  )?.toLowerCase() === "yes";
const hasHighBloodPressure = profile => {
  const {
    [BLOOD_PRESSURE_SYS]: systolic,
    [BLOOD_PRESSURE_DIA]: diastolic,
    [BLOOD_PRESSURE_UNKNOWN]: unknown
  } = profile[MEDICAL] ?? {};
  if (systolic == null || diastolic == null || unknown) return false;

  return systolic >= HIGH_SYSTOLIC || diastolic >= HIGH_DIASTOLIC;
};
export const isSuicidal = (answers, questions) => {
  const phq9b = findAnswerByQuestionTag(PHQ9B, answers, questions) === "Yes";
  const suicide1a =
    findAnswerByQuestionTag(SUICIDE1A, answers, questions) === "Yes";

  return phq9b || suicide1a;
};
